import { EnumHelper, assert, toHttpParams, toHttpHeaders, ShareResourceHelper } from '@sdpp-web/util';
import * as i0 from '@angular/core';
import { Injectable, Inject, InjectionToken, Optional } from '@angular/core';
import { shareReplay, map, tap, catchError, switchMap, first, takeUntil, filter, delay, mergeMap } from 'rxjs/operators';
import * as i1$1 from '@sdpp-web/portal';
import { SDPP_API_BASE } from '@sdpp-web/portal';
import * as i1 from '@angular/common/http';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { LogLevelHelper } from '@sdpp-web/logger';
import { of, EMPTY, BehaviorSubject, timer } from 'rxjs';

var MenuItemType;
(function (MenuItemType) {
    MenuItemType["Menu"] = "menu";
    MenuItemType["Route"] = "route";
    MenuItemType["Label"] = "labelOnly";
    MenuItemType["Link"] = "link";
    MenuItemType["MenuReference"] = "menu-reference";
})(MenuItemType || (MenuItemType = {}));
var MenuItemTypeHelper;
(function (MenuItemTypeHelper) {
    function from(name) {
        return EnumHelper.fromValue(MenuItemType, name);
    }
    MenuItemTypeHelper.from = from;
})(MenuItemTypeHelper || (MenuItemTypeHelper = {}));

class ApplicationContext {
}

class MenuItem {
}

class PortalContext {
}

class PortalContextService {
    constructor(_sdppApiBase, _http) {
        this._sdppApiBase = _sdppApiBase;
        this._http = _http;
    }
    getPortalContext() {
        const url = `${this._sdppApiBase}/user/context/portal`;
        return this._http.get(url).pipe(shareReplay(1));
    }
}
/** @nocollapse */ /** @nocollapse */ PortalContextService.ɵfac = function PortalContextService_Factory(t) { return new (t || PortalContextService)(i0.ɵɵinject(SDPP_API_BASE), i0.ɵɵinject(i1.HttpClient)); };
/** @nocollapse */ /** @nocollapse */ PortalContextService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: PortalContextService, factory: PortalContextService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PortalContextService, [{
            type: Injectable
        }], function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [SDPP_API_BASE]
                    }] }, { type: i1.HttpClient }];
    }, null);
})();

function PORTAL_CONTEXT_PROVIDERS() {
    return [
        {
            provide: PortalContextService
        }
    ];
}

/**
 * This class represents the agGrid's ColumnState interface.
 * It is replicated here so we do not force tha ag-grid-community library
 * to be set as a peer dependency of the @sdpp-web/user library.
 */
class ColumnState {
}

class ColumnConfiguration {
    constructor(data) {
        this.colId = data.colId;
        this.index = data.index;
        this.isPinned = data.isPinned;
        this.width = Math.round(data.width);
    }
    static fromColumnStates(columnStates) {
        const columnConfigurations = [...columnStates].sort(orderByVisibility).map(ColumnConfiguration.fromColumnState);
        return columnConfigurations;
    }
    static toColumnStates(columnConfigurations) {
        const states = [...columnConfigurations]
            .sort(orderByIndex)
            .map((conf) => conf.toColumnState());
        return states;
    }
    static fromColumnState(state, index) {
        return new ColumnConfiguration({
            colId: state.colId,
            index: state.hide ? -1 : index,
            isPinned: state.pinned === 'right' ? 'right' : state.pinned ? 'left' : null,
            width: state.width
        });
    }
    toColumnState() {
        const state = new ColumnState();
        state.colId = this.colId;
        state.hide = this.index === -1;
        state.pinned = this.isPinned;
        state.width = this.width;
        return state;
    }
}
/**
 * Orders by placing visible column states first and hidden column states last
 * @param a The first column state to compare
 * @param b The second column state to compare
 */
function orderByVisibility(a, b) {
    const aWeight = a.hide ? 1 : 0;
    const bWeight = b.hide ? 1 : 0;
    return aWeight - bWeight;
}
function orderByIndex(a, b) {
    return a.index - b.index;
}

class BlotterConfiguration {
    constructor(data) {
        this.availableColumns = (data.availableColumns || []).map((colConf) => new ColumnConfiguration(colConf));
        this.code = data.code;
    }
}

const HAS_CONDENSED_BLOTTER_SETTING_CODE = 'hasCondensedBlotterView';
const HAS_CONDENSED_BLOTTER_SETTING_VERSION = '1.0.0';

class Theme {
    constructor(dto) {
        this.id = dto.id;
        this.code = dto.code;
        this.name = dto.name;
        this.cssName = dto.cssName;
        this.isDefault = dto.isDefault;
    }
}

const USER_CONTEXT_PARAMS = new InjectionToken('user-context-params');

class User {
    constructor(data) {
        this.email = data.email;
        this.firstName = data.firstName;
        this.lastName = data.lastName;
        this.language = data.language;
        this.rights = data.rights;
        this.ssoId = data.ssoId;
    }
    /**
     * Gets a value indicating whether the current user has the specfified permission.
     * @param value Permission
     * @return True if the current user has it, otherwise false.
     */
    hasPermission(value) {
        return this.rights.indexOf(value) > -1;
    }
}

class UserContext {
    constructor(data) {
        this.user = new User(data.user);
        this.authenticatedUser = data.authenticatedUser && new User(data.authenticatedUser);
        this.blotterConfigurations = (data.blotterConfigurations || []).map((conf) => new BlotterConfiguration(conf));
        this.filterConfigurations = data.filterConfigurations || [];
        this.logLevel = data.logLevel ? LogLevelHelper.from(data.logLevel) : null;
        this.settings = data.settings;
        this.availableThemes = (data.themes || []).map(theme => new Theme(theme));
        this.currentTheme = data.theme && this.availableThemes.find(theme => theme.code === data.theme);
        this.authenticationLevel = data.authenticationLevel == null ? -1 : data.authenticationLevel;
        this.isAppEnabled = data.isAppEnabled;
    }
    /**
     * Indicates whether the user has a condensed blotter view or not.
     */
    get hasCondensedBlotterView() {
        if (this.settings && this.settings.global) {
            const setting = this.settings.global.find(gSetting => gSetting.code === HAS_CONDENSED_BLOTTER_SETTING_CODE &&
                gSetting.version === HAS_CONDENSED_BLOTTER_SETTING_VERSION);
            return !!setting && setting.value === 'true';
        }
        return false;
    }
}

class UserContextDto {
}

var AuthenticationLevel;
(function (AuthenticationLevel) {
    AuthenticationLevel[AuthenticationLevel["Unknown"] = -1] = "Unknown";
    AuthenticationLevel[AuthenticationLevel["Anonymous"] = 0] = "Anonymous";
    AuthenticationLevel[AuthenticationLevel["Low"] = 3] = "Low";
    AuthenticationLevel[AuthenticationLevel["Normal"] = 5] = "Normal";
    AuthenticationLevel[AuthenticationLevel["High"] = 9] = "High";
})(AuthenticationLevel || (AuthenticationLevel = {}));

/**
 * This class represents a user setting object.
 */
class UserSetting {
}
const PORTAL_APPLICATION_CODE = 'global';

const USER_SETTING_PARAMS = new InjectionToken('user-setting-params');

const apiVersion$4 = '1.0';
/**
 * This service allows management of user settings.
 */
class UserSettingService {
    /**
     * Creates an instance of {@link UserSettingService}.
     * @param _sdppApiBase Base URL for SDPP Api.
     * @param _userSettingParams User setting parameters, see {@link UserSettingParams}.
     * @param _http The HTTP client.
     */
    constructor(_sdppApiBase, _userSettingParams, _http) {
        this._sdppApiBase = _sdppApiBase;
        this._userSettingParams = _userSettingParams;
        this._http = _http;
    }
    /**
     * Gets the user settings.
     * @param input The input parameter, see {@link GetUserSettingInput}.
     */
    getUserSettings(input) {
        const appCode = input.appCode || this._userSettingParams.appCode;
        assert(appCode, `appCode must be provided in the getUserSettings input parameter or using the UserSettingModule.forRoot method`);
        const params = toHttpParams(Object.assign(Object.assign({}, input), { appCode }));
        const headers = toHttpHeaders({ apiVersion: apiVersion$4 });
        return this._http
            .get(this.getUrl('list'), { params, headers })
            .pipe(map((response) => response.settings));
    }
    /**
     * Add or update (upsert) user settings.
     * @param input The input parameter, see {@link UpsertUserSettingInput}.
     */
    upsertUserSetting(input) {
        const appCode = input.appCode || this._userSettingParams.appCode;
        assert(appCode, `appCode must be provided in the upsertUserSetting input parameter or using the UserSettingModule.forRoot method`);
        const headers = toHttpHeaders({ apiVersion: apiVersion$4 });
        return this._http
            .post(this.getUrl('save'), Object.assign(Object.assign({}, input), { appCode }), { headers })
            .pipe(map((response) => response.settings));
    }
    /**
     * Delete user settings.
     * @param input The input parameter, see {@link DeleteUserSettingInput}.
     */
    deleteUserSettings(input) {
        const appCode = input.appCode || this._userSettingParams.appCode;
        assert(appCode, `appCode must be provided in the deleteUserSettings input parameter or using the UserSettingModule.forRoot method`);
        const headers = toHttpHeaders({ apiVersion: apiVersion$4 });
        return this._http.post(this.getUrl('delete'), Object.assign(Object.assign({}, input), { appCode }), { headers });
    }
    getUrl(method) {
        return `${this._sdppApiBase}/user/setting/${method}`;
    }
}
/** @nocollapse */ /** @nocollapse */ UserSettingService.ɵfac = function UserSettingService_Factory(t) { return new (t || UserSettingService)(i0.ɵɵinject(SDPP_API_BASE), i0.ɵɵinject(USER_SETTING_PARAMS), i0.ɵɵinject(i1.HttpClient)); };
/** @nocollapse */ /** @nocollapse */ UserSettingService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserSettingService, factory: UserSettingService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserSettingService, [{
            type: Injectable
        }], function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [SDPP_API_BASE]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [USER_SETTING_PARAMS]
                    }] }, { type: i1.HttpClient }];
    }, null);
})();

const apiVersion$3 = '1.0';
/**
 * This service retrieves the user context for a specific application of portal
 */
class UserContextService {
    /**
     * Creates an instance of {@link UserContextService}.
     * @param _sdppApiBase Base URL for SDPP Api.
     * @param _userContextParams User context parameters, see {@link UserContextParams}.
     * @param _http The HTTP client.
     * @param _userSettingService The User setting service, see {@link UserSettingService}.
     */
    constructor(_sdppApiBase, _userContextParams, _http, _userSettingService) {
        this._sdppApiBase = _sdppApiBase;
        this._userContextParams = _userContextParams;
        this._http = _http;
        this._userSettingService = _userSettingService;
        this._context$s = {};
        this._userContexts = {};
    }
    get userDefaultContext() {
        return this._userContexts[this._userContextParams.appCode];
    }
    /**
     * Gets the user context for a specific application.
     * @param appCode The application code. Use "global" to get the portal user context.
     * If no appCode is provided, getUserContext will use the value provided in USER_CONTEXT_PARAMS injection token.
     */
    getUserContext(appCode) {
        appCode = appCode || this._userContextParams.appCode;
        if (!this._context$s[appCode]) {
            const url = `${this._sdppApiBase}/user/context`;
            const params = toHttpParams({ appCode });
            const headers = toHttpHeaders({ apiVersion: apiVersion$3 });
            this._context$s[appCode] = this._http
                .get(url, { params, headers })
                .pipe(map((data) => new UserContext(data)), tap((userContext) => {
                this._userContexts[appCode] = userContext;
            }), shareReplay(1));
        }
        return this._context$s[appCode];
    }
    /**
     * Sets a theme for a user.
     * @param themeCode The code of the theme.
     */
    setTheme(themeCode) {
        return this._http.post(`${this._sdppApiBase}/user/settheme?themeCode=${themeCode}`, null).pipe(map(() => void 0));
    }
    /**
     * Sets a language for a user.
     * @param languageCode The code of the language.
     */
    setLanguage(languageCode) {
        return this._http
            .post(`${this._sdppApiBase}/user/setlanguage?languageCode=${languageCode}`, null)
            .pipe(map(() => void 0));
    }
    /**
     * Sets ore removes the condensed blotter view mode for the user.
     * @param isCondensed [true] if user has a condensed bloter view, [false] otherwise.
     */
    setCondensedBlotterView(isCondensed) {
        return isCondensed
            ? this._userSettingService
                .upsertUserSetting({
                appCode: PORTAL_APPLICATION_CODE,
                value: 'true',
                code: HAS_CONDENSED_BLOTTER_SETTING_CODE,
                settingsVersion: HAS_CONDENSED_BLOTTER_SETTING_VERSION
            })
                .pipe(map(() => { }))
            : this._userSettingService
                .deleteUserSettings({
                appCode: PORTAL_APPLICATION_CODE,
                code: HAS_CONDENSED_BLOTTER_SETTING_CODE,
                settingsVersion: HAS_CONDENSED_BLOTTER_SETTING_VERSION
            })
                .pipe(map(() => { }));
    }
}
/** @nocollapse */ /** @nocollapse */ UserContextService.ɵfac = function UserContextService_Factory(t) { return new (t || UserContextService)(i0.ɵɵinject(SDPP_API_BASE), i0.ɵɵinject(USER_CONTEXT_PARAMS), i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(UserSettingService)); };
/** @nocollapse */ /** @nocollapse */ UserContextService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserContextService, factory: UserContextService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserContextService, [{
            type: Injectable
        }], function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [SDPP_API_BASE]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [USER_CONTEXT_PARAMS]
                    }] }, { type: i1.HttpClient }, { type: UserSettingService }];
    }, null);
})();

function USER_CONTEXT_PROVIDERS(params) {
    return [UserContextService, { provide: USER_CONTEXT_PARAMS, useValue: params }];
}

const SDPP_USER_CONTEXT_MOCK = new InjectionToken('SdppUserContextMock');
class UserContextServiceMock {
    constructor(_userContextDtoMock) {
        this._userContextDtoMock = _userContextDtoMock;
    }
    get userDefaultContext() {
        return this._userContext;
    }
    getUserContext() {
        this._userContext = new UserContext(this._userContextDtoMock);
        return of(this._userContext);
    }
    setTheme(_themeCode) {
        return EMPTY;
    }
    setLanguage(_languageCode) {
        return EMPTY;
    }
    setCondensedBlotterView(_isCondensed) {
        return EMPTY;
    }
}
/** @nocollapse */ /** @nocollapse */ UserContextServiceMock.ɵfac = function UserContextServiceMock_Factory(t) { return new (t || UserContextServiceMock)(i0.ɵɵinject(SDPP_USER_CONTEXT_MOCK)); };
/** @nocollapse */ /** @nocollapse */ UserContextServiceMock.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserContextServiceMock, factory: UserContextServiceMock.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserContextServiceMock, [{
            type: Injectable
        }], function () {
        return [{ type: UserContextDto, decorators: [{
                        type: Inject,
                        args: [SDPP_USER_CONTEXT_MOCK]
                    }] }];
    }, null);
})();

const USER_GRID_STATE_PARAMS = new InjectionToken('user-grid-state-params');

const apiVersion$2 = '1.0';
class UserGridStateIoService {
    /**
     * Creates an instance of {@link UserGridStateIoService}.
     * @param _sdppApiBase Base URL for SDPP Api.
     * @param _userGridStateParams User state parameters, see {@link UserGridStateParams}.
     * @param _http The HTTP client.
     */
    constructor(_sdppApiBase, _userGridStateParams, _http) {
        this._sdppApiBase = _sdppApiBase;
        this._userGridStateParams = _userGridStateParams;
        this._http = _http;
    }
    getState(keys) {
        const headers = toHttpHeaders({ apiVersion: apiVersion$2 });
        const body = {
            appCode: this._userGridStateParams.appCode,
            keys
        };
        return this._http.post(this.getUrl('get-state'), body, { headers });
    }
    saveState(states) {
        const headers = toHttpHeaders({ apiVersion: apiVersion$2 });
        const body = {
            appCode: this._userGridStateParams.appCode,
            states
        };
        return this._http
            .post(this.getUrl('save-state'), body, { headers })
            .pipe(catchError(() => of(null)));
    }
    getUrl(method) {
        return `${this._sdppApiBase}/grid/${method}`;
    }
}
/** @nocollapse */ /** @nocollapse */ UserGridStateIoService.ɵfac = function UserGridStateIoService_Factory(t) { return new (t || UserGridStateIoService)(i0.ɵɵinject(SDPP_API_BASE), i0.ɵɵinject(USER_GRID_STATE_PARAMS), i0.ɵɵinject(i1.HttpClient)); };
/** @nocollapse */ /** @nocollapse */ UserGridStateIoService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserGridStateIoService, factory: UserGridStateIoService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserGridStateIoService, [{
            type: Injectable
        }], function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [SDPP_API_BASE]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [USER_GRID_STATE_PARAMS]
                    }] }, { type: i1.HttpClient }];
    }, null);
})();

class UserGridStateService {
    constructor(_ioService) {
        this._ioService = _ioService;
    }
    getState(gridItems) {
        return this._ioService.getState(gridItems.map(grid => grid.key)).pipe(map((states) => gridItems.map((gridItem) => {
            const gridState = states.find(state => state.key === gridItem.key);
            const columnStates = gridState && gridState.columns
                ? gridState.columns.map((dto) => new ColumnConfiguration(dto))
                : null;
            const columns = this.mapToAvailableColumns(columnStates, gridItem.availableColumns);
            return {
                key: gridItem.key,
                code: (gridState && gridState.code) || gridItem.code,
                columns
            };
        })));
    }
    saveState(states) {
        return this._ioService.saveState(states);
    }
    mapToAvailableColumns(stateColumns, availableColumns) {
        // no state -> return available columns
        if (!stateColumns) {
            return availableColumns;
        }
        // state exists -> map state to available columns
        const stateColumnsMap = new Map(stateColumns.map((col) => [col.colId, col]));
        const result = availableColumns.map(col => stateColumnsMap.has(col.colId)
            ? stateColumnsMap.get(col.colId)
            : new ColumnConfiguration(Object.assign(Object.assign({}, col), { index: -1 })));
        return result;
    }
}
/** @nocollapse */ /** @nocollapse */ UserGridStateService.ɵfac = function UserGridStateService_Factory(t) { return new (t || UserGridStateService)(i0.ɵɵinject(UserGridStateIoService)); };
/** @nocollapse */ /** @nocollapse */ UserGridStateService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserGridStateService, factory: UserGridStateService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserGridStateService, [{
            type: Injectable
        }], function () { return [{ type: UserGridStateIoService }]; }, null);
})();

function USER_GRID_STATE_PROVIDERS(params) {
    return [UserGridStateService, UserGridStateIoService, { provide: USER_GRID_STATE_PARAMS, useValue: params }];
}

const SDPP_USER_GRID_STATE_MOCK = new InjectionToken('SdppUserGridStateMock');
class UserGridStateServiceMock {
    constructor(_gridStates) {
        this._gridStates = _gridStates;
    }
    getState() {
        return of(this._gridStates);
    }
    saveState() {
        return EMPTY;
    }
}
/** @nocollapse */ /** @nocollapse */ UserGridStateServiceMock.ɵfac = function UserGridStateServiceMock_Factory(t) { return new (t || UserGridStateServiceMock)(i0.ɵɵinject(SDPP_USER_GRID_STATE_MOCK, 8)); };
/** @nocollapse */ /** @nocollapse */ UserGridStateServiceMock.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserGridStateServiceMock, factory: UserGridStateServiceMock.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserGridStateServiceMock, [{
            type: Injectable
        }], function () {
        return [{ type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [SDPP_USER_GRID_STATE_MOCK]
                    }] }];
    }, null);
})();
const USER_GRID_STATE_SERVICE_MOCK_PROVIDERS = [
    {
        provide: UserGridStateService,
        useClass: UserGridStateServiceMock
    }
];

const USER_IMPERSONATION_PARAMS = new InjectionToken('sdpp-web-impersonation-params');

const USER_IMPERSONATION_SERVICE = new InjectionToken('sdpp-web-impersonation-service');

class UserImpersonationService {
    constructor(_userImpersonationParams) {
        this._userImpersonationParams = _userImpersonationParams;
    }
    /**
     * Gets the impersonated identity.
     *
     * @returns The impersonated identity.
     */
    getImpersonatedIdentity() {
        const cookieList = document.cookie.split(';');
        const cookieName = `${this._userImpersonationParams.header}=`;
        let value;
        for (let i = 0; i < cookieList.length; i += 1) {
            value = cookieList[i].replace(/^\s+/g, '');
            if (value.indexOf(cookieName) === 0) {
                return value.substring(cookieName.length, value.length);
            }
        }
        return null;
    }
    /**
     * Sets the impersonated identity.
     * @param identity The identity to use.
     */
    setImpersonatedIdentity(identity) {
        this.setCookie(identity);
    }
    /**
     * Removes the impersonated identity.
     */
    removeImpersonatedIdentity() {
        this.setCookie('', -1);
    }
    setCookie(value, expireDays = 7) {
        const date = new Date();
        date.setTime(date.getTime() + expireDays * 24 * 60 * 60 * 1000);
        const expires = `expires=${date.toUTCString()}`;
        const path = `; path=/`;
        document.cookie = `${this._userImpersonationParams.header}=${value}; ${expires}${path}`;
    }
}
/** @nocollapse */ /** @nocollapse */ UserImpersonationService.ɵfac = function UserImpersonationService_Factory(t) { return new (t || UserImpersonationService)(i0.ɵɵinject(USER_IMPERSONATION_PARAMS)); };
/** @nocollapse */ /** @nocollapse */ UserImpersonationService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserImpersonationService, factory: UserImpersonationService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserImpersonationService, [{
            type: Injectable
        }], function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [USER_IMPERSONATION_PARAMS]
                    }] }];
    }, null);
})();

class UserImpersonationInterceptorService {
    constructor(_userImpersonationParams, _userImpersonationService) {
        this._userImpersonationParams = _userImpersonationParams;
        this._userImpersonationService = _userImpersonationService;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    intercept(req, next) {
        const impersonatedIdentity = this._userImpersonationService.getImpersonatedIdentity();
        if (this._userImpersonationParams.enabled) {
            if (!impersonatedIdentity) {
                return next.handle(req);
            }
            return next.handle(req.clone({ setHeaders: { [this._userImpersonationParams.header]: impersonatedIdentity } }));
        }
        if (!!impersonatedIdentity) {
            // eslint-disable-next-line no-console
            console.warn(`User impersonation failed since the feature is disabled in this environment`);
        }
        return next.handle(req);
    }
}
/** @nocollapse */ /** @nocollapse */ UserImpersonationInterceptorService.ɵfac = function UserImpersonationInterceptorService_Factory(t) { return new (t || UserImpersonationInterceptorService)(i0.ɵɵinject(USER_IMPERSONATION_PARAMS), i0.ɵɵinject(USER_IMPERSONATION_SERVICE)); };
/** @nocollapse */ /** @nocollapse */ UserImpersonationInterceptorService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserImpersonationInterceptorService, factory: UserImpersonationInterceptorService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserImpersonationInterceptorService, [{
            type: Injectable
        }], function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [USER_IMPERSONATION_PARAMS]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [USER_IMPERSONATION_SERVICE]
                    }] }];
    }, null);
})();

function USER_IMPERSONATION_PROVIDERS(params) {
    return [
        { provide: USER_IMPERSONATION_PARAMS, useValue: params },
        { provide: USER_IMPERSONATION_SERVICE, useClass: UserImpersonationService },
        { provide: HTTP_INTERCEPTORS, useClass: UserImpersonationInterceptorService, multi: true }
    ];
}

const USER_SESSION_PARAMS = new InjectionToken('user-session-params');

const apiVersion$1 = '1.0';
/**
 * This service makes request to user states API.
 */
class UserSessionIoService {
    /**
     * Creates an instance of {@link UserSessionsIoService}.
     * @param _urlService The Url service
     * @param _http The HTTP client.
     */
    constructor(_urlService, _http) {
        this._urlService = _urlService;
        this._http = _http;
        this.contextKey = `userSessionIoService-${_urlService.baseSdppApi}`;
    }
    /**
     * Gets the user states.
     * @param input The input parameter, see {@link GetUserStateInput}.
     */
    getSessionTimeout() {
        const headers = toHttpHeaders({ apiVersion: apiVersion$1 });
        return this._http.get(this._urlService.getBackgroundUrl('/session/timeout'), { headers, responseType: 'text' });
    }
    /**
     * Sends a heartbeat to the server to check session state.
     * @returns The server date.
     */
    heartbeat() {
        const headers = toHttpHeaders({ apiVersion: apiVersion$1 });
        return this._http.get(this._urlService.getUrl('/session/heartbeat'), { headers, responseType: 'text' });
    }
}
/** @nocollapse */ /** @nocollapse */ UserSessionIoService.ɵfac = function UserSessionIoService_Factory(t) { return new (t || UserSessionIoService)(i0.ɵɵinject(i1$1.UrlService), i0.ɵɵinject(i1.HttpClient)); };
/** @nocollapse */ /** @nocollapse */ UserSessionIoService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserSessionIoService, factory: UserSessionIoService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserSessionIoService, [{
            type: Injectable
        }], function () { return [{ type: i1$1.UrlService }, { type: i1.HttpClient }]; }, null);
})();

var SessionState;
(function (SessionState) {
    SessionState["OPENED"] = "OPENED";
    SessionState["CLOSED"] = "CLOSED";
})(SessionState || (SessionState = {}));
/**
 * This service regularly checks the validity of the SSO session.
 * The observable getSessionTimeout() emits when the session expires.
 */
class UserSessionService {
    constructor(_ioService, _params) {
        this._ioService = _ioService;
        this._params = _params;
        this._sessionState$ = new BehaviorSubject(SessionState.OPENED);
        this.monitorSession();
    }
    /**
     * Gets an observable that emits the actual up-to-date session state (after a check on server).
     * @returns Observable<SessionState> The session state.
     */
    sessionState$() {
        return this.heartbeat$().pipe(switchMap(() => this._sessionState$), first());
    }
    /**
     * Gets an observable that emits session state changes. When the session is closed, the observable is completed.
     * @returns Observable<SessionState> The session state.
     */
    sessionStateChange$() {
        return this._sessionState$;
    }
    /**
     * Sends a heartbeat to the server at regular intervals (polling).
     * If the response throws an exception, we can consider the session was closed.
     *
     */
    monitorSession() {
        // session should by default expire at midnight
        of(void 0)
            .pipe(takeUntil(this._sessionState$.pipe(filter(state => state === SessionState.CLOSED))), delay(new Date(new Date().setHours(24, 0, 0, 0))))
            .subscribe(() => {
            this.emitSessionExpired();
        });
        /**
         * TODO:
         * defer + repeatWhen ensures calls to heartbeat won't be stacked (drawback of timer/interval)
         * ==> zone.js (zone-patch-rxjs) issue: repeatWhen/retryWhen operator throws unexpected exceptions.
         * https://github.com/angular/angular/issues/31684
         * https://github.com/angular/angular/issues/31687
         * When the issue will be fixed, uncomment the following code and remove the timer section.
         */
        // defer(() => this.heartbeat$())
        //   .pipe(
        //     repeatWhen(notifications => notifications.pipe(delay(this._params.heartbeatInterval))),
        //     takeUntil(this._sessionState$.pipe(filter(state => state === SessionState.CLOSE)))
        //   )
        //   .subscribe();
        timer(0, this._params.heartbeatInterval)
            .pipe(takeUntil(this._sessionState$.pipe(filter(state => state === SessionState.CLOSED))), switchMap(() => this.heartbeat$()))
            .subscribe();
    }
    heartbeat$() {
        return this._ioService.heartbeat().pipe(map(result => {
            try {
                // if response is not a valid date ==> consider session is expired
                const date = new Date(JSON.parse(result));
                if (isNaN(date.getTime())) {
                    throw Error();
                }
                return date;
            }
            catch (e) {
                throw Error('Session has expired.');
            }
        }), catchError(error => {
            // eslint-disable-next-line no-console
            console.error(error);
            this.emitSessionExpired();
            return EMPTY;
        }));
    }
    emitSessionExpired() {
        this._sessionState$.next(SessionState.CLOSED);
        this._sessionState$.complete();
    }
}
// versionKey is used for sharing instance between multiple web components. Increase when the API changes.
UserSessionService.versionKey = '2';
/** @nocollapse */ /** @nocollapse */ UserSessionService.ɵfac = function UserSessionService_Factory(t) { return new (t || UserSessionService)(i0.ɵɵinject(UserSessionIoService), i0.ɵɵinject(USER_SESSION_PARAMS)); };
/** @nocollapse */ /** @nocollapse */ UserSessionService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserSessionService, factory: UserSessionService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserSessionService, [{
            type: Injectable
        }], function () {
        return [{ type: UserSessionIoService }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [USER_SESSION_PARAMS]
                    }] }];
    }, null);
})();

const DEFAULT_SESSION_PARAMS = {
    heartbeatInterval: 2 * 60 * 1000 // 2 minutes
};
function USER_SESSION_PROVIDERS(params = DEFAULT_SESSION_PARAMS) {
    return [
        {
            provide: UserSessionService,
            useFactory: userSessionServiceFactory,
            deps: [UserSessionIoService, USER_SESSION_PARAMS]
        },
        UserSessionIoService,
        { provide: USER_SESSION_PARAMS, useValue: params }
    ];
}
// share UserSessionService between multiple web components if all dependencies have
/* eslint-disable import/no-deprecated */
function userSessionServiceFactory(ioService, params) {
    const key = `userSessionService-${UserSessionService.versionKey}-${ioService.contextKey}`;
    // backward compatbility
    window.sdppWebServices = window.sdppWebServices || {};
    const deprecatedUserSessionService = window.sdppWebServices[key];
    let userSessionService = ShareResourceHelper.get(key);
    if (deprecatedUserSessionService && !userSessionService) {
        userSessionService = deprecatedUserSessionService;
        ShareResourceHelper.save(key, userSessionService);
    }
    else if (!deprecatedUserSessionService && userSessionService) {
        window.sdppWebServices[key] = userSessionService;
    }
    else if (!deprecatedUserSessionService && !userSessionService) {
        userSessionService = new UserSessionService(ioService, params);
        ShareResourceHelper.save(key, userSessionService);
        window.sdppWebServices[key] = userSessionService;
    }
    return userSessionService;
}

class UserSessionServiceMock {
    sessionState$() {
        return EMPTY;
    }
    sessionStateChange$() {
        return EMPTY;
    }
}
const expiredUserSessionServiceMock = new UserSessionServiceMock();

/**
 * Input parameter of {@link UserSettingService#deleteUserSetting}.
 * Note: Filled properties are logical conjunctions (AND).
 */
class DeleteUserSettingInput {
}

/**
 * Input parameter of {@link UserSettingService#getUserSettings}.
 * Note: Filled properties are logical conjunctions (AND).
 */
class GetUserSettingInput {
}

/**
 * Input parameter of {@link UserSettingService#upsertUserSetting}.
 */
class UpsertUserSettingInput {
}

/**
 * Response parameter of {@link UserSettingService#getUserSettings}.
 */
class UserSettingResponse {
}

function USER_SETTING_PROVIDERS(params) {
    return [UserSettingService, { provide: USER_SETTING_PARAMS, useValue: params }];
}

/**
 * Input parameter of {@link UserStateService#deleteUserState}.
 * Note: Filled properties are logical conjunctions (AND).
 */
class DeleteUserStateInput {
}

/**
 * Input parameter of {@link UserStateService#getUserStates}.
 * Note: Filled properties are logical conjunctions (AND).
 */
class GetUserStateInput {
}

/**
 * Input parameter of {@link UserStateService#upsertUserState}.
 */
class UpsertUserStateInput {
}

/**
 * This class loads and update user states for a given context.
 */
class UserStateContextLoader {
    constructor(_context, _contextVersion, _ioService) {
        this._context = _context;
        this._contextVersion = _contextVersion;
        this._ioService = _ioService;
        this._state$ = new BehaviorSubject(new Map());
        this._loadState$ = this._ioService
            .getUserStates({
            context: this._context,
            contextVersion: this._contextVersion
        })
            .pipe(map(contexts => this.toMap(contexts)), tap(state => {
            this._state$.next(state);
        }), shareReplay(1));
    }
    /** Get all states of the context */
    getStates() {
        return this._loadState$.pipe(mergeMap(() => this._state$), first());
    }
    /** Get one state */
    getState(code) {
        return this.getStates().pipe(map(s => s.get(code)));
    }
    /** Save one state */
    saveState(code, value) {
        return this._ioService
            .upsertUserState({
            code,
            context: this._context,
            contextVersion: this._contextVersion,
            value
        })
            .pipe(mergeMap(() => this.getStates()), map(states => {
            const newStates = new Map(states);
            newStates.set(code, value);
            this._state$.next(newStates);
        }));
    }
    /** Delete one state */
    deleteState(code) {
        return this._ioService
            .deleteUserState({
            code,
            context: this._context,
            contextVersion: this._contextVersion
        })
            .pipe(mergeMap(() => this.getStates()), map(states => {
            const newStates = new Map(states);
            newStates.delete(code);
            this._state$.next(newStates);
        }));
    }
    /** Delete all states of the context */
    deleteStates() {
        return this._ioService
            .deleteUserState({
            context: this._context,
            contextVersion: this._contextVersion
        })
            .pipe(mergeMap(() => this.getStates()), map(() => {
            this._state$.next(new Map());
        }));
    }
    toMap(contexts) {
        return contexts.length >= 1 ? new Map(contexts[0].data.map((s) => [s.code, s.value])) : new Map();
    }
}

/**
 * This class represents a user state object.
 */
class UserStateContext {
}

const USER_STATE_PARAMS = new InjectionToken('user-state-params');

/**
 * Response parameter of {@link UserStateService#getUserStates}.
 */
class UserStateResponse {
}

/**
 * This class represents a user state object.
 */
class UserState {
}

const apiVersion = '1.0';
/**
 * This service makes request to user states API.
 */
class UserStateIoService {
    /**
     * Creates an instance of {@link UserStateIoService}.
     * @param _sdppApiBase Base URL for SDPP Api.
     * @param _userStateParams User state parameters, see {@link UserStateParams}.
     * @param _http The HTTP client.
     */
    constructor(_sdppApiBase, _userStateParams, _http) {
        this._sdppApiBase = _sdppApiBase;
        this._userStateParams = _userStateParams;
        this._http = _http;
    }
    /**
     * Gets the user states.
     * @param input The input parameter, see {@link GetUserStateInput}.
     */
    getUserStates(input) {
        const params = toHttpParams(Object.assign({ appCode: this._userStateParams.appCode }, input));
        const headers = toHttpHeaders({ apiVersion });
        return this._http
            .get(this.getUrl('getUserStates'), { params, headers })
            .pipe(map((response) => response.states));
    }
    /**
     * Add or update (upsert) a user state.
     * @param input The input parameter, see {@link UpsertUserStateInput}.
     */
    upsertUserState(input) {
        const headers = toHttpHeaders({ apiVersion });
        return this._http
            .post(this.getUrl('save'), Object.assign({ appCode: this._userStateParams.appCode }, input), { headers })
            .pipe(map((response) => response.states));
    }
    /**
     * Delete a user state.
     * @param input The input parameter, see {@link DeleteUserStateInput}.
     */
    deleteUserState(input) {
        const headers = toHttpHeaders({ apiVersion });
        return this._http.post(this.getUrl('delete'), Object.assign({ appCode: this._userStateParams.appCode }, input), { headers });
    }
    getUrl(method) {
        return `${this._sdppApiBase}/user/state/${method}`;
    }
}
/** @nocollapse */ /** @nocollapse */ UserStateIoService.ɵfac = function UserStateIoService_Factory(t) { return new (t || UserStateIoService)(i0.ɵɵinject(SDPP_API_BASE), i0.ɵɵinject(USER_STATE_PARAMS), i0.ɵɵinject(i1.HttpClient)); };
/** @nocollapse */ /** @nocollapse */ UserStateIoService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserStateIoService, factory: UserStateIoService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserStateIoService, [{
            type: Injectable
        }], function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [SDPP_API_BASE]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [USER_STATE_PARAMS]
                    }] }, { type: i1.HttpClient }];
    }, null);
})();

/**
 * This service allows management of user states
 *
 * Prerequisite: import the UserStateModule with the method `UserStateModule.forRoot`.
 */
class UserStateService {
    constructor(_ioService) {
        this._ioService = _ioService;
        this._contextState = new Map();
    }
    /** Get all states of the context */
    getStates(context, contextVersion) {
        return this.getContextState(context, contextVersion).getStates();
    }
    /** Get one state */
    getState(context, contextVersion, code) {
        return this.getContextState(context, contextVersion).getState(code);
    }
    /** Save one state */
    saveState(context, contextVersion, code, value) {
        return this.getContextState(context, contextVersion).saveState(code, value);
    }
    /** Delete one state */
    deleteState(context, contextVersion, code) {
        return this.getContextState(context, contextVersion).deleteState(code);
    }
    /** Delete all states of the context */
    deleteStates(context, contextVersion) {
        return this.getContextState(context, contextVersion).deleteStates();
    }
    getContextState(context, contextVersion) {
        return this.getMapValue(this.getMapValue(this._contextState, context, () => new Map()), contextVersion, () => new UserStateContextLoader(context, contextVersion, this._ioService));
    }
    getMapValue(map, key, defaultFactory) {
        if (!map.has(key)) {
            map.set(key, defaultFactory());
        }
        return map.get(key);
    }
}
/** @nocollapse */ /** @nocollapse */ UserStateService.ɵfac = function UserStateService_Factory(t) { return new (t || UserStateService)(i0.ɵɵinject(UserStateIoService)); };
/** @nocollapse */ /** @nocollapse */ UserStateService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserStateService, factory: UserStateService.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserStateService, [{
            type: Injectable
        }], function () { return [{ type: UserStateIoService }]; }, null);
})();

function USER_STATE_PROVIDERS(params) {
    return [UserStateService, UserStateIoService, { provide: USER_STATE_PARAMS, useValue: params }];
}

const SDPP_USER_STATE_MOCK = new InjectionToken('SdppUserStateMock');
class UserStateServiceMock {
    constructor(_states) {
        this._states = _states;
    }
    getStates() {
        return of(this._states);
    }
    getState(_context, _contextVersion, code) {
        return of(this._states && this._states.get(code));
    }
    saveState(_context, _contextVersion, code, value) {
        if (this._states) {
            this._states.set(code, value);
        }
        return of(void 0);
    }
    deleteState(_context, _contextVersion, code) {
        if (this._states) {
            this._states.delete(code);
        }
        return of(void 0);
    }
    deleteStates() {
        if (this._states) {
            this._states = null;
        }
        return of(void 0);
    }
}
/** @nocollapse */ /** @nocollapse */ UserStateServiceMock.ɵfac = function UserStateServiceMock_Factory(t) { return new (t || UserStateServiceMock)(i0.ɵɵinject(SDPP_USER_STATE_MOCK, 8)); };
/** @nocollapse */ /** @nocollapse */ UserStateServiceMock.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: UserStateServiceMock, factory: UserStateServiceMock.ɵfac });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserStateServiceMock, [{
            type: Injectable
        }], function () {
        return [{ type: Map, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [SDPP_USER_STATE_MOCK]
                    }] }];
    }, null);
})();
const USER_STATE_SERVICE_MOCK_PROVIDERS = [
    {
        provide: UserStateService,
        useClass: UserStateServiceMock
    }
];

/**
 * Generated bundle index. Do not edit.
 */

export { ApplicationContext, AuthenticationLevel, BlotterConfiguration, ColumnConfiguration, ColumnState, DeleteUserSettingInput, DeleteUserStateInput, GetUserSettingInput, GetUserStateInput, HAS_CONDENSED_BLOTTER_SETTING_CODE, HAS_CONDENSED_BLOTTER_SETTING_VERSION, MenuItem, MenuItemType, MenuItemTypeHelper, PORTAL_APPLICATION_CODE, PORTAL_CONTEXT_PROVIDERS, PortalContext, PortalContextService, SDPP_USER_CONTEXT_MOCK, SDPP_USER_GRID_STATE_MOCK, SDPP_USER_STATE_MOCK, SessionState, Theme, USER_CONTEXT_PARAMS, USER_CONTEXT_PROVIDERS, USER_GRID_STATE_PARAMS, USER_GRID_STATE_PROVIDERS, USER_GRID_STATE_SERVICE_MOCK_PROVIDERS, USER_IMPERSONATION_PARAMS, USER_IMPERSONATION_PROVIDERS, USER_IMPERSONATION_SERVICE, USER_SESSION_PROVIDERS, USER_SETTING_PARAMS, USER_SETTING_PROVIDERS, USER_STATE_PARAMS, USER_STATE_PROVIDERS, USER_STATE_SERVICE_MOCK_PROVIDERS, UpsertUserSettingInput, UpsertUserStateInput, User, UserContext, UserContextDto, UserContextService, UserContextServiceMock, UserGridStateIoService, UserGridStateService, UserGridStateServiceMock, UserImpersonationService, UserSessionIoService, UserSessionService, UserSessionServiceMock, UserSetting, UserSettingResponse, UserSettingService, UserState, UserStateContext, UserStateContextLoader, UserStateIoService, UserStateResponse, UserStateService, UserStateServiceMock, expiredUserSessionServiceMock, orderByIndex, orderByVisibility, userSessionServiceFactory };
